import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Chip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Tv as TvIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import ProgramasService, {
  Programa,
  ProgramaCreate,
  ProgramaUpdate,
  ProgramaFilter,
} from '../../services/programasService';

// Interface para entidades de referência
interface EntidadeSimples {
  id: number;
  nome: string;
}

const ProgramasPage: React.FC = () => {
  const theme = useTheme();
  
  // Estado de listagem
  const [programas, setProgramas] = useState<Programa[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [programasPrincipais, setProgramasPrincipais] = useState<EntidadeSimples[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<ProgramaFilter>({});
  
  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentPrograma, setCurrentPrograma] = useState<Programa | null>(null);
  const [newPrograma, setNewPrograma] = useState<ProgramaCreate>({ nome: '' });
  const [editPrograma, setEditPrograma] = useState<ProgramaUpdate>({});

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      
      // Buscar programas para uso como "programa principal"
      const programasData = await ProgramasService.getAllProgramas();
      setProgramasPrincipais(programasData);
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchProgramas = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const data = await ProgramasService.getProgramas(filter, skip, limit);
      setProgramas(data);
      
      // Obter contagem total para paginação
      const count = await ProgramasService.getProgramasCount(filter);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar programas:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar programas',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar programas ao iniciar a página ou mudar filtros/paginação
    fetchProgramas();
    // Carregar referências
    carregarReferencias();
  }, [fetchProgramas, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof ProgramaFilter, value: string | number | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Função para limpar filtro de busca
  const clearSearchFilter = () => {
    setFilter((prev) => ({
      ...prev,
      nome: undefined
    }));
    setPage(0);
  };

  const handleCreatePrograma = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newPrograma.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do programa é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await ProgramasService.createPrograma(newPrograma);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Programa criado com sucesso',
        severity: 'success',
      });
      
      // Limpar formulário
      setNewPrograma({ nome: '' });
      
      // Recarregar dados
      fetchProgramas();
      
      // Atualizar programas principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao criar programa:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar programa',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (programa: Programa) => {
    setCurrentPrograma(programa);
    // Inicializar dados para edição
    setEditPrograma({
      nome: programa.nome,
      mnemonico: programa.mnemonico,
      principal_id: programa.principal_id
    });
    
    setOpenEditDialog(true);
  };

  const handleEditPrograma = async () => {
    if (!currentPrograma) return;

    try {
      // Verificar campos obrigatórios
      if (!editPrograma.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do programa é obrigatório',
          severity: 'error',
        });
        return;
      }

      await ProgramasService.updatePrograma(currentPrograma.id, editPrograma);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Programa atualizado com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentPrograma(null);
      setEditPrograma({});
      
      // Recarregar dados
      fetchProgramas();
      
      // Atualizar programas principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao atualizar programa:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar programa',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (programa: Programa) => {
    setCurrentPrograma(programa);
    setOpenDeleteDialog(true);
  };

  const handleDeletePrograma = async () => {
    if (!currentPrograma) return;

    try {
      await ProgramasService.deletePrograma(currentPrograma.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Programa excluído com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentPrograma(null);
      
      // Recarregar dados
      fetchProgramas();
      
      // Atualizar programas principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao excluir programa:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir programa',
        severity: 'error',
      });
    }
  };

  // Handler para alterações em selects
  const handleSelectChange = (event: SelectChangeEvent<number | string | undefined>, setter: Function) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setter((prev: any) => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <SidebarLayout>
      <Box p={3}>
        <Typography variant="h4" gutterBottom display="flex" alignItems="center">
          <TvIcon sx={{ mr: 2 }} />
          Gerenciamento de Programas
        </Typography>
        
        {/* Área de filtros */}
        <Paper 
          elevation={2} 
          sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.default }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Buscar por nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filter.nome ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={clearSearchFilter}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Buscar por mnemônico"
                variant="outlined"
                size="small"
                value={filter.mnemonico || ''}
                onChange={(e) => handleFilterChange('mnemonico', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filter.mnemonico ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => handleFilterChange('mnemonico', undefined)}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="programa-principal-filter-label">Programa Principal</InputLabel>
                <Select
                  labelId="programa-principal-filter-label"
                  label="Programa Principal"
                  name="principal_id"
                  value={filter.principal_id || ''}
                  onChange={(e) => handleFilterChange('principal_id', e.target.value === '' ? undefined : Number(e.target.value))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {programasPrincipais.map((programa) => (
                    <MenuItem key={programa.id} value={programa.id}>
                      {programa.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{ height: '100%' }}
              >
                Novo Programa
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Programas */}
        <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Mnemônico</TableCell>
                  <TableCell>Programa Principal</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : programas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhum programa encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  programas.map((programa) => (
                    <TableRow key={programa.id} hover>
                      <TableCell>{programa.id}</TableCell>
                      <TableCell>{programa.nome}</TableCell>
                      <TableCell>{programa.mnemonico || '-'}</TableCell>
                      <TableCell>
                        {programasPrincipais.find(p => p.id === programa.principal_id)?.nome || '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(programa)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(programa)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo Programa</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newPrograma.nome || ''}
                onChange={(e) => setNewPrograma({ ...newPrograma, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mnemônico"
                name="mnemonico"
                value={newPrograma.mnemonico || ''}
                onChange={(e) => setNewPrograma({ ...newPrograma, mnemonico: e.target.value })}
                helperText="Código ou abreviação do programa"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-programa-principal-label">Programa Principal</InputLabel>
                <Select
                  labelId="new-programa-principal-label"
                  label="Programa Principal"
                  name="principal_id"
                  value={newPrograma.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewPrograma)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {programasPrincipais.map((programa) => (
                    <MenuItem key={programa.id} value={programa.id}>
                      {programa.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreatePrograma}
            disabled={!newPrograma.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar Programa</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={editPrograma.nome || ''}
                onChange={(e) => setEditPrograma({ ...editPrograma, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mnemônico"
                name="mnemonico"
                value={editPrograma.mnemonico || ''}
                onChange={(e) => setEditPrograma({ ...editPrograma, mnemonico: e.target.value })}
                helperText="Código ou abreviação do programa"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-programa-principal-label">Programa Principal</InputLabel>
                <Select
                  labelId="edit-programa-principal-label"
                  label="Programa Principal"
                  name="principal_id"
                  value={editPrograma.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditPrograma)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {programasPrincipais
                    .filter(programa => currentPrograma ? programa.id !== currentPrograma.id : true)
                    .map((programa) => (
                      <MenuItem key={programa.id} value={programa.id}>
                        {programa.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEditPrograma}
            disabled={!editPrograma.nome}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o programa "{currentPrograma?.nome}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeletePrograma}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default ProgramasPage;
